import React from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { newDocId } from '../doc/clipIds';
import { copySectionCutToChoruses, isChorus, resliceSection } from '../doc/sectionOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { timelineMessages } from '../messages/timelineMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import editorStyles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */
import styles from './RecutSectionTools.module.scss';

/** The cut lengths of the recut box (design c6: "2 beats · 4 · 8"). */
export const CUT_LENGTH_BEATS = [2, 4, 8] as const;

interface RecutSectionToolsProps {
  section: MusicVideoSongMapSection;
  onDone: () => void;
}

/**
 * The hand tools under a section recut (design c6, f2): "Use this cut for every chorus" (a chorus
 * only) and the cut length. They change the document at once, with no AI and no credits; Undo
 * takes them back.
 */
const RecutSectionToolsComponent: React.FC<RecutSectionToolsProps> = ({ section, onDone }) => {
  const intl = useIntl();
  const toast = useShowToast();
  const { change, songMap } = useMusicVideoEditor();
  const makeId = () => newDocId();
  return (
    <>
      <div className={editorStyles.separator} />
      {isChorus(section) && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            change((doc) => copySectionCutToChoruses(doc, songMap.sections, section.index, makeId));
            toast.showSuccess(intl.formatMessage(timelineMessages.everyChorusDone));
            onDone();
          }}
        >
          {intl.formatMessage(timelineMessages.everyChorus)}
        </button>
      )}
      <div className={styles.lengthRow}>
        <span>{intl.formatMessage(timelineMessages.cutLength)}</span>
        {CUT_LENGTH_BEATS.map((beats) => (
          <button
            key={beats}
            type="button"
            onClick={() => change((doc) => resliceSection(doc, section, songMap.beats, beats, makeId))}
          >
            {intl.formatMessage(timelineMessages.cutLengthBeats, { beats })}
          </button>
        ))}
      </div>
    </>
  );
};

export const RecutSectionTools = TypedMemo(RecutSectionToolsComponent);
